import {
  type DeviceKeyPairs,
  assertDeviceKeyPairsMatch,
} from "../../src/crypto/index.js";

const DATABASE_NAME = "binding-trust-witness-device-keys-v1";
const DATABASE_VERSION = 1;
const DEVICE_STORE_NAME = "devices";

export interface StoredDeviceKeys {
  readonly relationshipId: string;
  readonly partyId: string;
  readonly createdAtMs: number;
  readonly keys: DeviceKeyPairs;
}

interface IndexedDeviceRecord extends StoredDeviceKeys {
  readonly storageKey: string;
}

export interface DeviceKeyStore {
  load(relationshipId: string, partyId: string): Promise<StoredDeviceKeys | undefined>;
  save(record: StoredDeviceKeys): Promise<void>;
  delete(relationshipId: string, partyId: string): Promise<void>;
}

export class DeviceStoreError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "DeviceStoreError";
  }
}

export class IndexedDbDeviceStore implements DeviceKeyStore {
  private readonly factory: IDBFactory | undefined;
  private databasePromise: Promise<IDBDatabase> | undefined;

  public constructor(factory: IDBFactory | undefined = globalThis.indexedDB) {
    this.factory = factory;
  }

  public async load(
    relationshipId: string,
    partyId: string,
  ): Promise<StoredDeviceKeys | undefined> {
    const storageKey = deviceStorageKey(relationshipId, partyId);
    const record = await this.readRecord(storageKey);
    if (record === undefined) {
      return undefined;
    }
    await validateStoredRecord(record, relationshipId, partyId);
    return {
      relationshipId: record.relationshipId,
      partyId: record.partyId,
      createdAtMs: record.createdAtMs,
      keys: record.keys,
    };
  }

  public async save(record: StoredDeviceKeys): Promise<void> {
    validateIdentifiers(record.relationshipId, record.partyId);
    if (!Number.isSafeInteger(record.createdAtMs) || record.createdAtMs < 0) {
      throw new DeviceStoreError("Device key creation time is invalid");
    }
    await assertDeviceKeyPairsMatch(record.keys, record.keys.publicKeys.deviceKeyId);
    assertNonExtractablePrivateKeys(record.keys);
    const indexedRecord: IndexedDeviceRecord = {
      storageKey: deviceStorageKey(record.relationshipId, record.partyId),
      relationshipId: record.relationshipId,
      partyId: record.partyId,
      createdAtMs: record.createdAtMs,
      keys: record.keys,
    };
    const database = await this.openDatabase();
    await transactionPromise(database, "readwrite", (store) => store.put(indexedRecord));
  }

  public async delete(relationshipId: string, partyId: string): Promise<void> {
    const database = await this.openDatabase();
    await transactionPromise(database, "readwrite", (store) =>
      store.delete(deviceStorageKey(relationshipId, partyId)),
    );
  }

  private async readRecord(storageKey: string): Promise<IndexedDeviceRecord | undefined> {
    const database = await this.openDatabase();
    return transactionPromise<IndexedDeviceRecord | undefined>(
      database,
      "readonly",
      (store) => store.get(storageKey),
    );
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (this.factory === undefined) {
      return Promise.reject(
        new DeviceStoreError("Secure browser storage is unavailable in this context"),
      );
    }
    this.databasePromise ??= openDatabase(this.factory);
    return this.databasePromise;
  }
}

export function deviceStorageKey(relationshipId: string, partyId: string): string {
  validateIdentifiers(relationshipId, partyId);
  return JSON.stringify([relationshipId, partyId]);
}

async function validateStoredRecord(
  value: unknown,
  expectedRelationshipId: string,
  expectedPartyId: string,
): Promise<void> {
  if (value === null || typeof value !== "object") {
    throw new DeviceStoreError("Stored device keys are malformed");
  }
  const record = value as Partial<IndexedDeviceRecord>;
  if (
    record.storageKey !== deviceStorageKey(expectedRelationshipId, expectedPartyId) ||
    record.relationshipId !== expectedRelationshipId ||
    record.partyId !== expectedPartyId ||
    !Number.isSafeInteger(record.createdAtMs) ||
    (record.createdAtMs ?? -1) < 0 ||
    record.keys === undefined
  ) {
    throw new DeviceStoreError("Stored device keys do not match this relationship");
  }
  await assertDeviceKeyPairsMatch(
    record.keys,
    record.keys.publicKeys.deviceKeyId,
  );
  assertNonExtractablePrivateKeys(record.keys);
}

function assertNonExtractablePrivateKeys(keys: DeviceKeyPairs): void {
  if (
    keys.signingKeyPair.privateKey.extractable ||
    keys.wrappingKeyPair.privateKey.extractable
  ) {
    throw new DeviceStoreError("Stored private keys must be non-extractable");
  }
}

function validateIdentifiers(relationshipId: string, partyId: string): void {
  if (
    !/^[A-Za-z0-9._~:-]{1,256}$/u.test(relationshipId) ||
    !/^[A-Za-z0-9._~:-]{1,256}$/u.test(partyId)
  ) {
    throw new DeviceStoreError("Relationship and party identifiers are invalid");
  }
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DEVICE_STORE_NAME)) {
        database.createObjectStore(DEVICE_STORE_NAME, { keyPath: "storageKey" });
      }
    };
    request.onerror = () => {
      reject(new DeviceStoreError("Secure browser storage could not be opened"));
    };
    request.onblocked = () => {
      reject(new DeviceStoreError("Secure browser storage upgrade is blocked"));
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      resolve(database);
    };
  });
}

function transactionPromise<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(DEVICE_STORE_NAME, mode);
    const request = operation(transaction.objectStore(DEVICE_STORE_NAME));
    let result: T;
    request.onsuccess = () => {
      result = request.result;
    };
    request.onerror = () => {
      reject(new DeviceStoreError("Secure browser storage operation failed"));
    };
    transaction.oncomplete = () => resolve(result);
    transaction.onabort = () => {
      reject(new DeviceStoreError("Secure browser storage transaction was aborted"));
    };
    transaction.onerror = () => {
      reject(new DeviceStoreError("Secure browser storage transaction failed"));
    };
  });
}
